// src/components/Sections/ServicesSection.tsx
import React from 'react';
import { 
  Code2, 
  Cloud, 
  Shield, 
  Layers, 
  RefreshCw, 
  Users,
  BookOpen,
  Lightbulb
} from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';

const ServicesSection: React.FC = () => {
  const { state } = useAppContext();
  
  // Translation helper
  const t = (sv: string, en: string) => state.language === 'sv' ? sv : en;

  const services = [
    {
      icon: Layers,
      title: t('Systemarkitektur & Design', 'System Architecture & Design'),
      description: t(
        'Designar skalbar arkitektur med mikrotjänster, Domain-Driven Design och moderna patterns. Expert på Clean Architecture och SOLID-principer.',
        'Designing scalable architecture with microservices, Domain-Driven Design and modern patterns. Expert in Clean Architecture and SOLID principles.'
      ),
      features: [
        t('Mikrotjänstarkitektur', 'Microservices Architecture'),
        'Domain-Driven Design',
        t('Event-driven arkitektur', 'Event-driven Architecture'),
        'Clean Architecture'
      ],
      color: 'blue'
    },
    {
      icon: Code2,
      title: t('Full Stack-utveckling', 'Full Stack Development'),
      description: t(
        'End-to-end utveckling med .NET Core backend och moderna JavaScript-ramverk. Från databas till användargränssnitt.',
        'End-to-end development with .NET Core backend and modern JavaScript frameworks. From database to user interface.'
      ),
      features: [
        '.NET Core & C#',
        'React, Vue & Angular',
        'SQL Server & Entity Framework',
        'REST APIs & SignalR'
      ],
      color: 'indigo'
    },
    {
      icon: Cloud,
      title: t('Cloud & DevOps', 'Cloud & DevOps'),
      description: t(
        'Azure-lösningar med fokus på skalbarhet, säkerhet och automatisering. CI/CD pipelines och containerisering.',
        'Azure solutions focusing on scalability, security and automation. CI/CD pipelines and containerization.'
      ),
      features: [
        'Azure Cloud Services',
        'Docker & Kubernetes',
        'Azure DevOps Pipelines',
        'Infrastructure as Code'
      ],
      color: 'purple'
    },
    {
      icon: RefreshCw,
      title: t('Legacy-modernisering', 'Legacy Modernization'),
      description: t(
        'Migrering från äldre system till moderna lösningar. Expertis inom både gamla och nya teknologier.',
        'Migration from older systems to modern solutions. Expertise in both old and new technologies.'
      ),
      features: [
        t('Migration till .NET Core', 'Migration to .NET Core'),
        t('Monolith till mikrotjänster', 'Monolith to Microservices'),
        t('Databasmodernisering', 'Database Modernization'),
        'API-first approach'
      ],
      color: 'green'
    },
    {
      icon: Shield,
      title: t('Säkerhet & Compliance', 'Security & Compliance'),
      description: t(
        'DevSecOps-approach med säkerhet inbyggt från start. Erfarenhet från bank- och myndighetsuppdrag.',
        'DevSecOps approach with security built in from the start. Experience from banking and government assignments.'
      ),
      features: [
        'OAuth 2.0 & JWT',
        'Security Headers',
        'OWASP Best Practices',
        t('AML/KYC-system', 'AML/KYC Systems')
      ],
      color: 'red'
    },
    {
      icon: Lightbulb,
      title: t('Teknisk rådgivning', 'Technical Consulting'),
      description: t(
        'Senior konsultation och mentorskap. Lead Developer-erfarenhet från komplexa projekt.',
        'Senior consultation and mentorship. Lead Developer experience from complex projects.'
      ),
      features: [
        t('Arkitekturgenomgångar', 'Architecture Reviews'),
        'Code Reviews',
        t('Team-mentorskap', 'Team Mentorship'),
        t('Best Practices-implementering', 'Best Practices Implementation')
      ],
      color: 'yellow'
    }
  ];

  const getColorClasses = (color: string) => {
    const colors: { [key: string]: { bg: string; text: string; border: string } } = {
      blue: {
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        text: 'text-blue-600 dark:text-blue-400',
        border: 'border-blue-200 dark:border-blue-800'
      },
      indigo: {
        bg: 'bg-indigo-100 dark:bg-indigo-900/30',
        text: 'text-indigo-600 dark:text-indigo-400',
        border: 'border-indigo-200 dark:border-indigo-800'
      },
      purple: {
        bg: 'bg-purple-100 dark:bg-purple-900/30',
        text: 'text-purple-600 dark:text-purple-400',
        border: 'border-purple-200 dark:border-purple-800'
      },
      green: {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-600 dark:text-green-400',
        border: 'border-green-200 dark:border-green-800'
      },
      red: {
        bg: 'bg-red-100 dark:bg-red-900/30',
        text: 'text-red-600 dark:text-red-400',
        border: 'border-red-200 dark:border-red-800'
      },
      yellow: {
        bg: 'bg-yellow-100 dark:bg-yellow-900/30',
        text: 'text-yellow-600 dark:text-yellow-400',
        border: 'border-yellow-200 dark:border-yellow-800'
      }
    };
    return colors[color] || colors.blue;
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6">
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              {t('Tjänster', 'Services')}
            </span>
          </div>
          
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('Hur jag kan hjälpa dig', 'How I can help you')}
          </h2>
          
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            {t(
              'Med 25 års erfarenhet levererar jag högkvalitativa lösningar inom systemutveckling, arkitektur och teknisk rådgivning',
              'With 25 years of experience, I deliver high-quality solutions in system development, architecture and technical consulting'
            )}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            const colors = getColorClasses(service.color);
            
            return (
              <div
                key={index}
                className="bg-white dark:bg-gray-900 rounded-xl p-6 hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 hover:scale-105"
              >
                <div className={`w-14 h-14 ${colors.bg} rounded-lg flex items-center justify-center mb-4`}>
                  <Icon className={`h-7 w-7 ${colors.text}`} />
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {service.description}
                </p>
                
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className={`${colors.text} mr-2 mt-1`}>•</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Recent highlights */}
        <div className="mt-16 bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            {t('Senaste uppdragen', 'Recent Assignments')}
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                Svenska Kraftnät
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t(
                  'Lead Developer för flödesbaserade kapacitetsberäkningar med SignalR och mikrotjänster',
                  'Lead Developer for flow-based capacity calculations with SignalR and microservices'
                )}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                Svea Bank
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t(
                  'AML/KYC-system med Domain-Driven Design och händelsedriven arkitektur',
                  'AML/KYC system with Domain-Driven Design and event-driven architecture'
                )}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <BookOpen className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                {t('Teknisk författare', 'Technical Author')}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t(
                  'Publicerat bok om Full Stack-utveckling med 12 tillhörande GitHub-projekt',
                  'Published book on Full Stack development with 12 accompanying GitHub projects'
                )}
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t(
              'Behöver du hjälp med ditt nästa projekt?',
              'Need help with your next project?'
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToContact}
              className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
            >
              {t('Kontakta mig', 'Contact me')}
            </button>
            <a
              href="tel:+46708940567"
              className="border-2 border-gray-300 hover:border-blue-600 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-8 py-4 rounded-lg font-semibold transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              📞 +46 708 940 567
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;